import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import {
  CapitanDocumentElementTypes,
  ClimberInfoDocumentSectionTypes,
  ClimberInfoRequiredDocumentElementTypes,
  ContentDocumentElementTypes,
  CustomQuestionDocumentElementTypes,
  DocumentElementType,
  DocumentPolicy,
  DocumentPolicyStatus,
  DocumentSectionType,
  DocumentTemplate,
  DocumentTemplateElement,
  DocumentTemplateSection,
  MinorOnlyDocumentElementTypes,
  getDocumentElementTypeDisplay,
  getDocumentSectionTypeDisplay
} from '../../../../../../../models/document-template';
import { DocumentTemplateService } from '../../../../../../../services/document-template/document-template.service';
import { arrayMove } from '../../../../../../../utils';
import { AccountService } from '../../../../../../../services/account/account.service';
import { CustomCustomerField, OrganizationConfig } from '../../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';

@Component({
  selector: 'app-document-template-edit-sections',
  templateUrl: './document-template-edit-sections.component.html',
  styleUrls: ['./document-template-edit-sections.component.css']
})
export class DocumentTemplateEditSectionsComponent implements OnInit {

  constructor(
    private accountService: AccountService,
    private documentTemplateService: DocumentTemplateService,
    private orgConfigService: OrganizationConfigService
  ) {}

  @Input()
  documentTemplate: DocumentTemplate;

  @Input()
  documentTemplateErrors: any;

  @Input()
  orgConfig: OrganizationConfig;

  @Output()
  documentTemplateChange = new EventEmitter<DocumentTemplate>();

  @Output()
  unsavedChanges = new EventEmitter<void>();

  public documentPolicies: DocumentPolicy[] = null;
  public customCustomerFields: CustomCustomerField[] = null;

  public documentPoliciesLoading = true;
  public customCustomerFieldsLoading = true;

  public serverError: number = null;

  public selectedElementType: DocumentElementType = null;
  public selectedElementTypePolicyId: number = null;
  public selectedElementTypeFieldId: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public getDocumentElementTypeDisplay = getDocumentElementTypeDisplay;
  public DocumentSectionType = DocumentSectionType;
  public DocumentElementType = DocumentElementType;
  public CapitanDocumentElementTypes = CapitanDocumentElementTypes;
  public CustomQuestionDocumentElementTypes = CustomQuestionDocumentElementTypes;
  public ContentDocumentElementTypes = ContentDocumentElementTypes;
  public ClimberInfoDocumentSectionTypes = ClimberInfoDocumentSectionTypes;

  // A map providing human readable versions of some common error messages.
  public HUMAN_READABLE_ERROR_MESSAGES = {
    'Missing required parameter: \'question_text\'': 'Question text may not be empty.',
    'Missing required parameter: \'label_text\'': 'Label text may not be empty.',
    '\'question_choices\' must not be empty': 'Questions must have at least one choice.'
  };

  ngOnInit() {
    this.loadPolicies();
    this.loadCustomCustomerFields();
  }

  public loadPolicies() {
    this.documentPoliciesLoading = true;

    this.documentTemplateService
      .getDocumentPolicies(1, 100)
      .then(policies => {
        this.documentPolicies = policies.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.documentPoliciesLoading = false; });
  }

  private loadCustomCustomerFields() {
    this.customCustomerFieldsLoading = true;

    this.orgConfigService
      .getCustomCustomerFields(1, 3000)
      .then(result => { this.customCustomerFields = result.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.customCustomerFieldsLoading = false; });
  }

  public getActivePolicies() {
    return this.documentPolicies.filter(policy => {
      return policy.status === DocumentPolicyStatus.active;
    });
  }

  public toggleSelectedElementType(elementType: DocumentElementType) {
    // Policy and custom field selections are handled by the separate
    // toggleSelectedElementTypePolicy function.
    this.selectedElementTypePolicyId = null;
    this.selectedElementTypeFieldId = null;

    if (this.selectedElementType === elementType) {
      this.selectedElementType = null;
    } else {
      this.selectedElementType = elementType;
    }
  }

  public toggleSelectedElementTypePolicy(policy: DocumentPolicy) {
    const policyAlreadySelected: boolean = (
      (this.selectedElementType === DocumentElementType.policy) &&
      (this.selectedElementTypePolicyId === policy.id)
    );

    if (policyAlreadySelected) {
      this.selectedElementType = null;
      this.selectedElementTypePolicyId = null;
    } else {
      this.selectedElementType = DocumentElementType.policy;
      this.selectedElementTypePolicyId = policy.id;
      this.selectedElementTypeFieldId = null;
    }
  }

  public toggleSelectedElementTypeCustomCustomerField(field: CustomCustomerField) {
    const fieldAlreadySelected: boolean = (
      (this.selectedElementType === DocumentElementType.customCustomerField) &&
      (this.selectedElementTypeFieldId === field.id)
    );

    if (fieldAlreadySelected) {
      this.selectedElementType = null;
      this.selectedElementTypeFieldId = null;
    } else {
      this.selectedElementType = DocumentElementType.customCustomerField;
      this.selectedElementTypeFieldId = field.id;
      this.selectedElementTypePolicyId = null;
    }
  }

  public nonPageBreakSections(): DocumentTemplateSection[] {
    return this.documentTemplate.sections.filter(
      (section) => section.section_type !== DocumentSectionType.pageBreak
    );
  }

  public participantInformationSections(): DocumentTemplateSection[] {
    return this.documentTemplate.sections.filter(
      (section) => {
        return [
          DocumentSectionType.adultClimberInfo,
          DocumentSectionType.minorClimberInfo,
          DocumentSectionType.unsupervisedMinorClimberInfo,
          DocumentSectionType.adultNonClimberInfo,
          DocumentSectionType.supervisedAdultClimberInfo,
          DocumentSectionType.supervisingClimberInfo,
          DocumentSectionType.supervisedClimberInfo,
          DocumentSectionType.climberInfo
        ].includes(section.section_type);
      }
    );
  }

  public sectionsForElementType(elementType: DocumentElementType): DocumentTemplateSection[] {
    if (MinorOnlyDocumentElementTypes.includes(elementType)) {
      return this.participantInformationSections().filter(
        (section) => section.section_type === DocumentSectionType.minorClimberInfo
      );
    }
    return this.participantInformationSections();
  }

  public sectionContainsElementType(elementType: DocumentElementType,
                                    section: DocumentTemplateSection): boolean {
    for (const element of section.elements) {
      if (element.element_type === elementType) { return true; }
    }
    return false;
  }

  public sectionContainsCustomField(field: CustomCustomerField,
                                    section: DocumentTemplateSection): boolean {
    for (const element of section.elements) {
      const elementIsField = (
        (element.element_type === DocumentElementType.customCustomerField) &&
        (element.parameters.field_id === field.id)
      );

      if (elementIsField) {
        return true;
      }
    }
    return false;
  }

  public addElementTypeToSection(elementType: DocumentElementType,
                                 section: DocumentTemplateSection) {
    section.elements.push(this.createBlankElement(elementType));

    this.selectedElementType = null;

    this.unsavedChanges.emit();
  }

  public addElementTypePolicyToSection(policy: DocumentPolicy,
                                       section: DocumentTemplateSection) {
    const policyElement = this.createBlankElement(DocumentElementType.policy);
    policyElement.parameters.policy_id = policy.id;
    policyElement.parameters.content = policy.content;

    // Policies do not start in edit mode, unlike other elements.
    policyElement.editing = false;

    section.elements.push(policyElement);

    this.selectedElementType = null;

    this.unsavedChanges.emit();
  }

  public addElementTypeCustomCustomerFieldToSection(field: CustomCustomerField,
                                                    section: DocumentTemplateSection) {
    const element = this.createBlankElement(DocumentElementType.customCustomerField);
    element.parameters.field_id = field.id;
    element.parameters.name = field.name;
    element.parameters.field_type = field.field_type;
    element.parameters.options = field.options;

    section.elements.push(element);

    this.selectedElementType = null;

    this.unsavedChanges.emit();
  }

  public createBlankElement(elementType: DocumentElementType) {
    const element: DocumentTemplateElement = {
      element_type: elementType,
      parameters: {},
      editing: true
    };

    if (elementType === DocumentElementType.shortText) {
      element.parameters.label_text = '';
      element.parameters.required = false;
      element.parameters.help_text = '';
    }

    if (elementType === DocumentElementType.paragraphInput) {
      element.parameters.label_text = '';
      element.parameters.required = false;
      element.parameters.help_text = '';
    }

    if (elementType === DocumentElementType.singleCheckbox) {
      element.parameters.required = false;
      element.parameters.help_text = '';
    }

    if (elementType === DocumentElementType.quizQuestion) {
      element.parameters.question_choices = ['Yes', 'No'];
      element.parameters.question_correct_answers = [];
      element.parameters.required = true;
      element.parameters.help_text = '';
    }

    if (elementType === DocumentElementType.signature) {
      element.parameters.required = true;
    }

    if (CapitanDocumentElementTypes.includes(elementType)) {
      element.parameters.required = false;
      element.parameters.help_text = '';
    }

    if (elementType === DocumentElementType.capitanAddress) {
      element.parameters.post_code_only = false;
    }

    if (elementType === DocumentElementType.capitanEmergencyContact) {
      element.parameters.number_of_contacts = 1;
      element.parameters.collect_email = false;
      element.parameters.collect_relation = false;
    }

    return element;
  }

  public getErrorForElement(sectionIdx, elementIdx): string {
    if (!this.documentTemplateErrors) { return null; }
    if (!this.documentTemplateErrors.sections) { return null; }
    const sectionsErrors = this.documentTemplateErrors.sections;

    if (!sectionsErrors[sectionIdx]) { return null; }
    if (!sectionsErrors[sectionIdx][elementIdx]) { return null; }

    const errorMessage = sectionsErrors[sectionIdx][elementIdx];

    if (this.HUMAN_READABLE_ERROR_MESSAGES[errorMessage]) {
      return this.HUMAN_READABLE_ERROR_MESSAGES[errorMessage];
    } else {
      return errorMessage;
    }
  }

  public sectionInEditMode(section: DocumentTemplateSection) {
    return section.editingName;
  }

  public getSectionDisplayName(section: DocumentTemplateSection) {
    if (section.section_type === DocumentSectionType.custom) {
      return section.name;
    } else {
      return getDocumentSectionTypeDisplay(section);
    }
  }

  public moveSectionToTop(sectionIdx: number) {
    arrayMove(
      this.documentTemplate.sections,
      sectionIdx,
      0
    );

    this.unsavedChanges.emit();
  }

  public moveSectionUp(sectionIdx: number) {
    arrayMove(
      this.documentTemplate.sections,
      sectionIdx,
      sectionIdx - 1
    );

    this.unsavedChanges.emit();
  }

  public moveSectionDown(sectionIdx: number) {
    arrayMove(
      this.documentTemplate.sections,
      sectionIdx,
      sectionIdx + 1
    );

    this.unsavedChanges.emit();
  }

  public moveSectionToBottom(sectionIdx: number) {
    arrayMove(
      this.documentTemplate.sections,
      sectionIdx,
      this.documentTemplate.sections.length - 1
    );

    this.unsavedChanges.emit();
  }

  public removeSection(sectionIdx: number) {
    this.documentTemplate.sections.splice(sectionIdx, 1);

    this.unsavedChanges.emit();
  }

  public canRemoveElement(element: DocumentTemplateElement,
                          section: DocumentTemplateSection): boolean {
    if (section.section_type === DocumentSectionType.custom) {
      return true;
    }

    return !ClimberInfoRequiredDocumentElementTypes.includes(element.element_type);
  }

  public moveElementUp(elementIdx: number, section: DocumentTemplateSection) {
    arrayMove(
      section.elements,
      elementIdx,
      elementIdx - 1
    );

    this.unsavedChanges.emit();
  }

  public moveElementDown(elementIdx: number, section: DocumentTemplateSection) {
    arrayMove(
      section.elements,
      elementIdx,
      elementIdx + 1
    );

    this.unsavedChanges.emit();
  }

  public removeElement(elementIdx: number, section: DocumentTemplateSection) {
    section.elements.splice(elementIdx, 1);

    this.unsavedChanges.emit();
  }

  public addSection() {
    this.documentTemplate.sections.push({
      name: 'Custom Section',
      section_type: DocumentSectionType.custom,
      elements: []
    });

    this.unsavedChanges.emit();
  }

  public addPageBreak() {
    this.documentTemplate.sections.push({
      name: '',
      section_type: DocumentSectionType.pageBreak,
      elements: []
    });

    this.unsavedChanges.emit();
  }

}
